import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Scale, Library, Sparkles, FolderHeart, Users, LogIn, UserCircle, FileText, BookOpen, Search, Camera, ChevronDown } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { client } from "@/lib/api";
import TypewriterHeadline from "@/components/TypewriterHeadline";
import HeroBackground from "@/components/HeroBackground";

// Frases rotativas do headline animado — decisão editorial (Prof. Paulo).
// Iter175: agora carregadas de ``/api/hero-phrases`` pelo admin,
// permitindo mudar frases + links via gerenciador sem redeploy.
// Estas ficam como FALLBACK durante o 1º render (LCP intacto) e
// caso a API falhe ou esteja vazia.
const DEFAULT_PHRASES = [
  {
    text: "A videoteca do Direito Público brasileiro.",
    highlight: "Direito Público",
    href: "/videos",
    ariaLabel: "Explorar o acervo de vídeos — a videoteca do Direito Público brasileiro",
  },
  {
    text: "Um laboratório vivo de aplicação de novas tecnologias ao estudo do direito.",
    highlight: "laboratório vivo",
    href: "/radar",
    ariaLabel: "Ver os radares — um laboratório vivo de novas tecnologias no direito",
  },
  {
    text: "Infraestrutura didática e de pesquisa em Direito Público brasileiro.",
    highlight: "Direito Público",
    href: "/guia-professores",
    ariaLabel: "Ver o Guia para Professores — infraestrutura didática e de pesquisa em Direito Público",
  },
];

export default function Hero({ stats }) {
  const { user } = useAuth();
  const collectionsTarget = user ? "/minhas-colecoes" : "/sobre-colecoes";

  // Iter175 — frases carregadas do admin (fallback = DEFAULT_PHRASES).
  // O estado inicial já é o default, então o LCP paint mostra a 1ª
  // frase completa (zero regressão). Quando a API responder, se as
  // frases diferirem do default, substituímos — o TypewriterHeadline
  // se re-arma naturalmente pelo dep no useEffect.
  const [phrases, setPhrases] = useState(DEFAULT_PHRASES);
  useEffect(() => {
    let alive = true;
    client.get("/hero-phrases").then((r) => {
      if (!alive) return;
      const items = r.data?.items || [];
      if (items.length === 0) return; // fallback já é o default
      // Normaliza snake_case → camelCase e filtra campos que o
      // TypewriterHeadline entende.
      setPhrases(items.map((p) => ({
        text: p.text,
        highlight: p.highlight || undefined,
        href: p.href || undefined,
        ariaLabel: p.aria_label || undefined,
      })));
    }).catch(() => { /* fallback silencioso */ });
    return () => { alive = false; };
  }, []);

  return (
    <section data-testid="hero" className="relative min-h-[92vh] flex items-end overflow-hidden pt-28 sm:pt-32">
      {/* Iter294 + Iter295.4 — Fundo "Fluxo de Conhecimento Jurídico"
          + Constelação Analítica (nuvem de dados SVG animada). O layout
          voltou a ser central/imponente após a diretiva do usuário —
          o elemento gráfico agora vive dentro deste componente de fundo
          e não compete com o texto. 100% GPU-composited, zero peso. */}
      <HeroBackground />

      <div className="relative z-10 max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-10 xl:px-14 2xl:px-20 pb-16 md:pb-24">
        <div className="max-w-3xl space-y-6 fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-red-500/40 bg-red-500/10 backdrop-blur">
            <Sparkles className="w-3.5 h-3.5 text-red-400" />
            <span className="text-[11px] uppercase tracking-[0.25em] font-semibold text-red-300">
              Plataforma Acadêmica
            </span>
          </div>

          <h1
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.08] tracking-tight min-h-[3em] sm:min-h-[3em] lg:min-h-[2.6em]"
            data-testid="hero-headline"
          >
            <TypewriterHeadline
              phrases={phrases}
              isStatic
              cursorClassName="text-red-400"
              linkClassName="text-white hover:text-red-50"
              testId="hero-typewriter"
            />
          </h1>

          <p className="text-base md:text-lg text-slate-200 max-w-xl leading-relaxed font-light">
            Podcasts, palestras, debates e textos de alguns dos maiores juristas do país: um acervo classificado, pesquisável e compartilhável — ideal para o uso em sala de aula, pesquisas acadêmicas e atividades de extensão.
          </p>

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <button
              type="button"
              onClick={() => {
                const el = document.getElementById("ultimos-episodios") || document.querySelector('[data-testid="home-quicknav"]');
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="btn-primary"
              data-testid="hero-cta-explore-content"
            >
              <Sparkles className="w-4 h-4" /> Explorar conteúdos
            </button>
            <Link to="/busca" className="btn-ghost" data-testid="hero-cta-explore">
              <Search className="w-4 h-4" /> Buscar no acervo
            </Link>
            <Link to={collectionsTarget} className="btn-ghost !border-red-500/40 hover:!border-red-500/80 hover:!bg-red-500/10" data-testid="hero-cta-collections">
              <FolderHeart className="w-4 h-4 text-red-400" /> Criar coleção compartilhável
            </Link>
            {user ? (
              <Link
                to="/minhas-colecoes"
                className="btn-ghost !border-sky-500/40 hover:!border-sky-500/80 hover:!bg-sky-500/10"
                data-testid="hero-cta-account"
              >
                <UserCircle className="w-4 h-4 text-sky-300" /> Minha conta
              </Link>
            ) : (
              <Link
                to="/conta"
                className="btn-ghost !border-sky-500/40 hover:!border-sky-500/80 hover:!bg-sky-500/10"
                data-testid="hero-cta-login"
              >
                <LogIn className="w-4 h-4 text-sky-300" /> Login
              </Link>
            )}
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 pt-8 max-w-2xl border-t border-white/10">
            <Stat label="Podcasts" value={stats?.podcasts ?? 6} loading={!stats} icon={<Library className="w-4 h-4 text-sky-400" />} to="/podcasts" />
            <Stat label="Episódios" value={stats?.episodes} loading={!stats} icon={<Play className="w-4 h-4 text-red-400" />} to="/videos" />
            <Stat label="Autores" value={stats?.authors} loading={!stats} icon={<Users className="w-4 h-4 text-red-400" />} to="/autores" />
            <Stat label="Artigos" value={stats?.articles} loading={!stats} icon={<FileText className="w-4 h-4 text-sky-400" />} to="/colunistas" />
            <Stat label="Livros" value={stats?.books} loading={!stats} icon={<BookOpen className="w-4 h-4 text-amber-300" />} to="/livraria" />
            <Stat label="Temas" value={stats?.tags} loading={!stats} icon={<Scale className="w-4 h-4 text-sky-400" />} to="/temas" />
          </div>
        </div>
      </div>

      {/* Indicador de rolagem — seta discreta pulsando, sinaliza que há
          muito conteúdo abaixo da dobra. Clique rola para a barra de
          atalhos / primeira seção. */}
      <button
        type="button"
        aria-label="Rolar para ver mais conteúdo"
        data-testid="hero-scroll-indicator"
        onClick={() => {
          const el = document.querySelector('[data-testid="home-quicknav"]') || document.getElementById("ultimos-episodios");
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
        className="absolute left-1/2 -translate-x-1/2 bottom-5 z-20 flex flex-col items-center gap-1 text-slate-400 hover:text-red-300 transition-colors animate-bounce"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Explore</span>
        <ChevronDown className="w-6 h-6" />
      </button>
    </section>
  );
}

function Stat({ label, value, icon, to, loading = false }) {
  const valueNode = loading ? (
    <span
      className="inline-block h-7 w-10 rounded bg-slate-700/70 animate-pulse align-middle"
      aria-label="Carregando"
    />
  ) : (
    value
  );
  const content = (
    <>
      <div className="flex items-center gap-1 text-[9px] sm:text-[10px] uppercase tracking-widest text-slate-400 group-hover:text-sky-300 transition-colors">
        {icon} <span className="truncate">{label}</span>
      </div>
      <div className="font-serif text-2xl sm:text-3xl font-bold text-white mt-1 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:text-red-400">
        {valueNode}
      </div>
    </>
  );

  if (to) {
    const isHash = to.startsWith("/#");
    if (isHash) {
      return (
        <a
          href={to.substring(1)}
          data-testid={`hero-stat-${label.toLowerCase()}`}
          className="group pt-4 block cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/60 rounded"
        >
          {content}
        </a>
      );
    }
    return (
      <Link
        to={to}
        data-testid={`hero-stat-${label.toLowerCase()}`}
        className="group pt-4 block cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/60 rounded"
      >
        {content}
      </Link>
    );
  }

  return <div className="pt-4">{content}</div>;
}
